<template>
  <transition
    name="dropdown"
    :enter-from-class="$style['dropdown-enter-from']"
    :enter-to-class="$style['dropdown-enter-to']"
    :leave-from-class="$style['dropdown-leave-from']"
    :leave-to-class="$style['dropdown-leave-to']">
    <div
      v-show="props.show && subItems.length > 0"
      :class="$style['dropdown']">
      <div :class="$style['dropdown__background']" />
      <div
        :class="$style['dropdown__container']"
        @mouseenter="$emit('on-enter')"
        @mouseleave="$emit('on-leave')">
        <div :class="$style['dropdown__inner']">
          <div :class="$style['navigation']">
            <div
              :class="[
                $style['navigation__inner'],
                {
                  [$style['navigation__inner--small']]: subItems.length > 12,
                },
              ]"
              tag="div">
              <NuxtLink
                v-for="item in subItems"
                :key="item.id"
                :no-prefetch="true"
                :class="[
                  $style['navigation__item'],
                  `dropdown-navigation__item--${item.slug.replace('/', '-')}`,
                  {
                    [$style['navigation__item--active']]: isActive(item.slug)
                  }
                ]"
                :aria-label="item.title"
                :to="{
                  path: getLinkPath(item)
                }"
                @click="$emit('close')">
                <span> {{ item.title ?? item[0].title }}</span>
              </NuxtLink>
            </div>
          </div>
          <div
            v-if="highlights"
            :class="$style['highlights']"
            tag="div">
            <LazyToolbarHighlight
              v-for="item in highlights"
              :key="item.uniqueId"
              :image="item.image"
              :aria-label="item.title"
              :to="getLinkPath(item)"
              :title="item.titles[$lang]"
              @click="$emit('close')" />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
<script setup>
defineEmits(['on-enter', 'on-leave', 'close'])
const props = defineProps({
  type: {
    type: String,
    default: null
  },
  navId: {
    type: Number,
    default: null
  },
  show: {
    type: [Number, Boolean],
    default: false
  }
})
const { $lang } = useNuxtApp()
const { timedNavigation } = useTimedNavigation()
const { navigation } = useNavigationStore()
const route = useRoute()
const subItems = ref([])
const highlights = ref([])

watch(() => props.navId, () => {
  const found = timedNavigation({ navigation: unref(navigation) }).find(item => item.id === props.navId)
  subItems.value = found?.subItems.filter(item => item.valid) || []
  highlights.value = found?.highlights.filter(item => item.valid) || []
})

const isActive = slug => {
  return (
    slug === route.path.substring(route.path.indexOf('/', 2) + 1)
  )
}
const getLinkPath = item => {
  const slug = item.slugs[$lang]
  return `/${$lang}/${slug}`
}

</script>
<style lang="scss" module>
$dropdown-height: 296px;
$colums-width: 260px;

.dropdown {
  background-color: $white;
  display: flex;
  justify-content: center;
  box-shadow: $box-shadow;
  min-height: $dropdown-height;
  height: fit-content;
  width: 100%;
  max-height:50vw;
  position: relative;

  &__background{
    position: absolute;
    transition: opacity $transition-time-slowest $transition-easing;
    width: 100vw;
    height: 100vh;
    top: 100%;
    left: 0;
    background-color: rgba($black, 0.25);
  }

  &__container{
    width: 100%;
    transition: opacity $transition-time-slowest $transition-easing;
  }

  &__inner {
    width: $breakpoint-desktop;
    padding: $spacing * 5;
    display: flex;
    justify-content: center;
    gap: $spacing * 5;
    margin: 0 auto;

    .navigation,
    .highlights {
      width: 50%;
    }

    .navigation{
      &__inner {
        display: grid;
        grid-template-rows: repeat(6, auto);
        grid-auto-columns: $colums-width;
        justify-content: end;
        grid-auto-flow: column;
        grid-gap: $spacing * 1.5 $spacing * 4;

        &--small{
          grid-auto-columns: 180px;
        }
      }

      &__item {
        color: $grey;
        font-weight: $font-weight-bold;
        font-size: $font-size;
        text-decoration: none;
        white-space: wrap;
        width: fit-content;
        transition: all $transition-time $transition-easing;

        &--active {
          color: $grey-darkest;
        }

        &:hover {
          color: $black;
        }
      }
    }

    .highlights {
      max-height: 216px;
      display: grid;
      grid-template-columns: repeat(auto-fit, $colums-width);
      grid-gap: $spacing * 2;

    }
  }
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transition: all $transition-time-slow linear;
}

.dropdown-enter-from,
.dropdown-leave-to {
    opacity: 0;
    transition: all $transition-time-slow linear;
}

.dropdown-enter-to .dropdown__container,
.dropdown-leave-from .dropdown__container,
  .dropdown-enter-to .dropdown__background,
.dropdown-leave-from .dropdown__background{
  opacity: 1;
}

.dropdown-enter-from .dropdown__container,
.dropdown-leave-to .dropdown__container,
.dropdown-enter-from .dropdown__background,
.dropdown-leave-to .dropdown__background{
    opacity: 0;

}

</style>
